<template>
  <div class="ab__registration">
    <h2 v-if="isEditMode">{{ $t('ab.title') }} ({{ $t('ab.pageEdit') }})</h2>
    <h2 v-else>{{ $t('ab.title') }} - {{ $t('ab.url') }} ({{ $t('ab.basic') }})</h2>
    <div class="heatmap__control_guide">
      <span>{{$t('common.guide.title')}}</span>
      <a :href="goLink" target="_blank">{{$t('common.guide.more')}}</a>
      <i class="icon icon--bc-step"></i>
    </div>

    <div class="ab-page__set">
      <div class="a-page-info--wrap">
        <div class="label">
          <i>A</i>
        </div>
        <div class="info">
          <div class="input--wrap">
            <label for="aPageName">
              {{ $t('ab.addOrEdit.registration.label.aPageName') }}
            </label>
            <input id="aPageName" type="text" readonly class="readonly" :value="applyData.pageName">
          </div>
          <div class="input--wrap">
            <label for="aPageUrl">
              {{ $t('ab.addOrEdit.registration.label.aPageUrl') }}
            </label>
            <input id="aPageUrl" type="text" readonly class="readonly" :value="applyData.url">
          </div>
        </div>
      </div>

      <div class="b-page-info--wrap">
        <div class="label">
          <i>B</i>
        </div>
        <div class="info">
          <div class="input--wrap">
            <label for="bPageName">
              {{ $t('ab.addOrEdit.registration.label.bPageName') }}
            </label>
            <input id="bPageName" type="text"
                   v-model.trim="model.pageName"
                   :class="{error: validation.bPageNameNull}">
            <i v-if="model.pageName.length"
               class="icon icon--close14"
               @click="model.pageName = ''"></i>
            <p v-if="validation.bPageNameNull" class="validation__text">
              {{ $t('common.validationErrorText.ab.pageName.required') }}
            </p>
          </div>
          <div class="input--wrap">
            <label for="bPageUrl">
              {{ $t('ab.addOrEdit.registration.label.bPageUrl') }}
            </label>
            <input v-if="isEditMode" type="text" class="readonly" :value="model.url" readonly>
            <input v-else id="bPageUrl" type="text"
                   v-model.trim="model.url"
                   :class="{error: validation.bPageUrlFormatError || validation.bPageUrlNull || validation.DomainNotMatch}">
            <i v-if="model.url.length && !isEditMode"
               class="icon icon--close14"
               @click="model.url = ''"></i>
            <p v-if="validation.bPageUrlNull || validation.bPageUrlFormatError" class="validation__text">
              {{ $t('common.validationErrorText.urlInstallChecker.required') }}
            </p>
            <p v-if="validation.DomainNotMatch" class="validation__text"
               v-html="$t('common.validationErrorText.urlInstallChecker.required')"></p>
          </div>
        </div>
      </div>
    </div>

    <div class="ab-option__set">
      <div class="ab-traffic--wrap">
        <p class="desc"
           v-html="$t('ab.addOrEdit.registration.traffic.desc', { remainTraffic: availableTraffic })"></p>
        <div class="fig--wrap">
          <div class="ab-traffic-area">
            <div class="option--wrap">
              <span class="label" v-html="$t('ab.addOrEdit.registration.traffic.label')"></span>
              <div class="input--wrap type__range">
                <div class="range">
                  <label for="aPageTraffic">A</label>
                  <range-slider
                    id="aPageTraffic"
                    class="slider a-page"
                    :class="{ readonly: isEditMode && !isEditDate }"
                    :disabled="isEditMode && !isEditDate"
                    :min="0"
                    :max="isEditMode && !isEditDate ? 50 : availableTraffic / 2"
                    :step="1"
                    :value="halfTraffic"
                    @input="onSliderInput"/>
                </div>
                <div class="range">
                  <label for="bPageTraffic">B</label>
                  <range-slider
                    id="bPageTraffic"
                    class="slider b-page"
                    :class="{ readonly: isEditMode && !isEditDate }"
                    :disabled="isEditMode && !isEditDate"
                    :min="0"
                    :max="isEditMode && !isEditDate ? 50 : availableTraffic / 2"
                    :step="1"
                    :value="halfTraffic"
                    @input="onSliderInput"/>
                </div>
              </div>
              <i class="icon icon--link-red-size20"></i>

              <div v-if="isEditMode && !isEditDate" class="input--wrap type__number">
                <input type="number"
                       min="0"
                       max="100"
                       class="readonly"
                       readonly="readonly"
                       :value="model.traffic"> %
              </div>
              <div v-else class="input--wrap type__number">
                <input type="number"
                       min="0"
                       :max="availableTraffic"
                       v-model.number="model.traffic"> %
                <span>/ {{ availableTraffic }}%</span>
              </div>
              <figure class="fig-traffic-graph--wrap">
                <app-chart chartType="pie"
                           class="pie-chart"
                           :width="180"
                           :height="180"
                           :chartOptions="isEditMode && !isEditDate? pieChartOptionsTypeEdit : pieChartOptionsTypeAdd">
                </app-chart>
              </figure>
              <ul class="legend--wrap">
                <li v-if="!isEditMode || isEditDate">
                  <i class="circle color-used"></i>
                  <span class="value">{{ abUsedTraffic }}%</span>
                  <span>{{ $t('ab.addOrEdit.registration.traffic.existingTraffic.label') }}</span>
                  <ui-icon-tooltip iconSize="normal" arrowBottom arrowRight>
                    <span v-html="$t('ab.addOrEdit.registration.traffic.existingTraffic.tooltip')"></span>
                  </ui-icon-tooltip>
                </li>
                <li>
                  <i class="circle color-a-page"></i>
                  <span class="value">{{ halfTraffic }}%</span>
                  <span>A {{ $t('common.page') }}</span>
                </li>
                <li>
                  <i class="circle color-b-page"></i>
                  <span class="value">{{ halfTraffic }}%</span>
                  <span>B {{ $t('common.page') }}</span>
                </li>
              </ul>
            </div>

            <p class="emphysize" v-html="$t('ab.addOrEdit.registration.traffic.emphysize')"></p>
          </div>

          <div class="ab-period--wrap">
            <p class="title">
              {{ $t('common.analysisPeriod') }}
              <ui-icon-tooltip iconSize="normal" arrowBottom arrowCenter>
                <span v-html="$t('ab.addOrEdit.registration.period.tooltip')"></span>
              </ui-icon-tooltip>
            </p>
            <ul>
              <li class="timezone--wrap" v-if="isEditMode && !isEditDate">
                <span class="input__label">{{ $t('common.timezone') }}</span>
                <div class="start__wrap">
                  {{applyData.timezoneName}}
                </div>
              </li>
              <li :class="{'disable': isEditMode && !isEditDate}">
                <span class="input__label" :class="{'start_select': !isEditMode || isEditDate, 'disable': isEditMode && !isEditDate}">{{ $t('common.startDate') }}</span>
                <div class="start__wrap" v-if="isEditMode && !isEditDate">
                  {{UIFormatDate(model.startDate)}}
                </div>
                <div class="start__wrap" v-else>
                  <label for="startDateInstantly" >
                    <input id="startDateInstantly" type="radio" name="startDate" value="true" :checked="startDateType === 'instantly'"
                           @change="onStartDateOptionChange('instantly')"><i class="icon icon--radio-off"></i>
                    <span>{{ $t('common.instantlyStart') }}</span>
                  </label>
                  <label for="startDateSelect">
                    <input id="startDateSelect" type="radio" name="startDate" value="false" :checked="startDateType === 'select'"
                           @change="onStartDateOptionChange('select')"><i class="icon icon--radio-off"></i>
                    <span>{{ $t('common.selectDate') }}</span>
                    <div v-if="startDateType === 'select'" class="datepicker--wrap data__start-date">
                      <input type="text" readonly
                             :value="formatDate(model.startDate)"
                             @click.stop.prevent="toggleDatePicker('start')">
                      <i class="icon icon--calendar-input"></i>
                      <date-picker v-if="showStartDatePicker && startDateType === 'select'"
                                   :value="model.startDate"
                                   :minDate="minStartDate"
                                   :maxDate="maxStartDate"
                                   :timezone="applyData.timezone"
                                   :timezoneName="applyData.timezoneName"
                                   @input="onCalendarDateChange('startDate', $event)"
                                   @onCloseClick="showStartDatePicker = false"/>
                    </div>
                  </label>
                </div>

              </li>
              <li>
                <span class="input__label">{{ $t('common.endDate') }}</span>
                <label for="endDateSelect">
                  <div class="datepicker--wrap data__end-date">
                    <input type="text" readonly
                           :value="formatDate(model.endDate)"
                           :disabled="isEditMode && !isEditEndDate"
                           @click.stop.prevent="toggleDatePicker('end')">
                    <i class="icon icon--calendar-input" :class="{'disabled': isEditMode && !isEditEndDate}"></i>
                    <date-picker v-if="showEndDatePicker"
                                 :value="model.endDate"
                                 :minDate="minEndDate"
                                 :timezone="applyData.timezone"
                                 :timezoneName="applyData.timezoneName"
                                 @input="onCalendarDateChange('endDate', $event)"
                                 @onCloseClick="showEndDatePicker = false"/>
                  </div>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>

    <transition name="fade">
      <ABreportConversionTarget v-if="!isEditMode || targetList.length !== 0 || isEditTarget" :isEditMode="isEditTarget" :type="isEditMode ? 'edit': 'new' " :list.sync="targetList" @onTargetUpdate="onTargetUpdate" />
    </transition>

    <div class="buttons--wrap">
      <div></div>
      <div class="button--wrap">
        <ui-button class="btn__cancel" size="large" @click="onAddOrEditCancel">{{ $t('common.prev') }}</ui-button>
      </div>
      <div class="button--wrap end">
        <ui-button v-if="isEditMode" class="btn__register" :class="{disabled: !hasTraffic}" :disabled="!hasTraffic" size="large" primary @click="onModify">{{ $t('common.edit') }}</ui-button>
        <ui-button v-else
                   class="btn__register"
                   primary
                   size="large"
                   :disabled="!hasTraffic"
                   :class="{disabled: !hasTraffic}"
                   @click="onRegistration">
          {{ $t('common.register') }}
        </ui-button>
      </div>
    </div>

    <!-- 트래킹 코드 -->
    <TrackingCodeConfirm
      v-if="showTrackingCode"
      @closePopup="showTrackingCode = false"
      :sid="applyData.sid">
      <button class="btn__confirm" @click="onAddorEditDone()" slot="somethingAction">
        <span class="btn-text">{{ $t('common.confirm') }}</span>
      </button>
    </TrackingCodeConfirm>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { addABTarget, addPage, getABTargetList, modifyABTarget, modifyPage } from '@/api/abtest'
import { isValidUrl } from '@/libs/validation-helper'
import AppChart from '@/components/chart'
import TrackingCodeConfirm from '@/components/dashboard/TrackingCodeConfirm.vue'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import { getDialog } from '@/libs/vue-utils'
import UiButton from '@/components/ui/UiButton'
import DatePicker from '@/components/date-picker'

import RangeSlider from 'vue-range-slider'
import 'vue-range-slider/dist/vue-range-slider.scss'
import { addDays, endOfDay, format, parse, startOfDay, subDays } from 'date-fns'
import {
  getOffset,
  triggerClickEvent,
  utcToLocalTimezone
} from '@/libs/utils'
import ABreportConversionTarget from '@/components/report-ab/ABreportConversionTarget.vue'
const DATE_FORAMT = 'YYYY-MM-DD HH:mm:ss'
const END_DATE_DIFF = 29

const openDialog = getDialog('customData')

export default {
  name: 'ABAddOrEdit',
  components: {
    ABreportConversionTarget,
    RangeSlider,
    AppChart,
    TrackingCodeConfirm,
    UiIconTooltip,
    UiButton,
    DatePicker
  },
  data () {
    return {
      showTrackingCode: false,
      showStartDatePicker: false,
      showEndDatePicker: false,
      validation: {
        bPageNameNull: false,
        bPageUrlNull: false,
        bPageUrlFormatError: false,
        DomainNotMatch: false
      },
      model: {
        pageName: '',
        url: '',
        traffic: 0,
        timezone: null,
        timezoneName: null,
        startDate: null,
        endDate: null
      },
      startDateType: 'instantly',
      abAid: null,
      abTestType: 'url',
      isEditDate: false,
      isEditEndDate: false,
      targetList: [],
      isEditTarget: true
    }
  },
  async created () {
    if (this.isEditMode) {
      this.abAid = this.$route.params.abAid

      this.targetList = await this.getTargetList(this.abAid)

      const bData = this.abList.find(item => item.aid === this.abAid)

      const now = utcToLocalTimezone(new Date(), this.applyData.timezoneName)

      const today = now
      const startDate = bData.startDate ? utcToLocalTimezone(bData.startDate, this.applyData.timezoneName) : subDays(now, 1)
      const endDate = bData.endDate ? utcToLocalTimezone(bData.endDate, this.applyData.timezoneName) : addDays(now, END_DATE_DIFF + 1)

      const state = bData.state

      if (startDate > today && !['endByPvLimit', 'endByAnalysisDate'].includes(state)) {
        this.isEditDate = true
        this.startDateType = 'select'
      }

      if (this.formatDate(endDate) >= this.formatDate(today) && !['endByPvLimit', 'endByAnalysisDate'].includes(state)) {
        this.isEditEndDate = true
      }

      if (!['uninstall', 'scheduledRun', 'error'].includes(state)) {
        this.isEditTarget = false
      }

      this.model.pageName = bData.pageName
      this.model.url = bData.url
      this.model.traffic = bData.traffic
      this.currentTraffic = JSON.parse(JSON.stringify(bData.traffic))
      if (bData.uiState === 'scheduledRun') {
        this.currentTraffic = this.currentTraffic + (100 - this.abUsedTraffic)
      }
      this.model.timezone = bData.timezone || this.applyData.timezone
      this.model.timezoneName = bData.timezoneName || this.applyData.timezoneName
      this.model.startDate = this.UIFormatDate(startDate)
      this.model.endDate = this.UIFormatDate(endDate)
    } else {
      if (this.availableTraffic <= 20) { //  in AB page add mode, set traffic default value
        this.model.traffic = this.availableTraffic
      } else {
        this.model.traffic = 20
      }
      this.model.timezone = this.applyData.timezone
      this.model.timezoneName = this.applyData.timezoneName
      this.model.startDate = this.formatDate(utcToLocalTimezone(new Date(), this.applyData.timezoneName))
      this.model.endDate = this.formatDate(addDays(utcToLocalTimezone(new Date(), this.applyData.timezoneName), END_DATE_DIFF))
    }
  },
  computed: {
    ...mapState([
      'userData',
      'applyData',
      'abList'
    ]),
    ...mapGetters([
      'abUsedTraffic',
      'lnbMinimize'
    ]),
    timezone () {
      return getOffset(this.applyData.timezoneName)
    },
    aid () {
      return this.$route.params.aid
    },
    isEditMode () {
      return this.$route.name === 'ab.edit'
    },
    availableTraffic () {
      return this.isEditMode ? this.currentTraffic : 100 - this.abUsedTraffic
    },
    hasTraffic () {
      return this.model.traffic
    },
    halfTraffic () {
      return this.model.traffic ? this.model.traffic / 2 : 0
    },
    pieChartOptionsTypeAdd () {
      // tooltip 사용하지 않으므로 i18n 적용 불필요
      return {
        tooltip: {
          show: false
        },
        legend: {
          show: false
        },
        emphasis: {
          show: false
        },
        series: [
          {
            name: '트래픽',
            type: 'pie',
            radius: ['30%', '100%'],
            avoidLabelOverlap: false,
            label: {
              normal: {
                show: false
              },
              emphasis: {
                show: false
              }
            },
            labelLine: {
              normal: {
                show: false
              }
            },
            data: [
              { value: this.abUsedTraffic, name: '사용중' },
              { value: this.halfTraffic, name: 'A페이지' },
              { value: this.halfTraffic, name: 'B페이지' },
              { value: 100 - this.abUsedTraffic - this.model.traffic, name: '가용 트래픽' }
            ]
          }
        ],
        color: ['#555555', '#9fce12', '#1199af', '#ebebeb']
      }
    },
    pieChartOptionsTypeEdit () {
      // tooltip 사용하지 않으므로 i18n 적용 불필요
      return {
        tooltip: {
          show: false
        },
        legend: {
          show: false
        },
        emphasis: {
          show: false
        },
        series: [
          {
            name: '트래픽',
            type: 'pie',
            radius: ['30%', '100%'],
            avoidLabelOverlap: false,
            label: {
              normal: {
                show: false
              },
              emphasis: {
                show: false
              }
            },
            labelLine: {
              normal: {
                show: false
              }
            },
            data: [
              { value: this.halfTraffic, name: 'A페이지' },
              { value: this.halfTraffic, name: 'B페이지' },
              { value: 100 - this.model.traffic, name: '가용 트래픽' }
            ]
          }
        ],
        color: ['#9fce12', '#1199af', '#ebebeb']
      }
    },
    validationUrl () {
      return isValidUrl(this.model.url)
    },
    validationDomain () {
      let originalDomain = this.applyData.real_domain.replace('www.', '')
      let parser = document.createElement('a')
      parser.href = this.model.url
      let hostname = parser.hostname.replace('www.', '')
      return originalDomain === hostname
    },
    inStartDate () {
      return new Date()
    },
    minStartDate () {
      if (!this.model.endDate) {
        return null
      }
      if (this.startDateType === 'instantly') {
        return utcToLocalTimezone(new Date(), this.applyData.timezoneName)
      } else {
        return addDays(utcToLocalTimezone(new Date(), this.applyData.timezoneName), 1)
      }
      // return new Date()
    },
    maxStartDate () {
      if (!this.model.endDate) {
        return null
      }
      return parse(this.model.endDate)
    },
    minEndDate () {
      if (!this.model.startDate) {
        return new Date()
      } else {
        return parse(this.model.startDate)
      }
    },
    goLink () {
      const lang = this.$i18n.locale

      if (lang === 'en') {
        return 'https://forum.beusable.net/en/solomon/post/1859'
      } else if (lang === 'ja') {
        return 'https://forum.beusable.net/ja/solomon/post/1858'
      } else {
        return 'https://forum.beusable.net/ko/solomon/post/1857'
      }
    }
  },
  watch: {
    lnbMinimize () {
      const appClassName = document.querySelector('#app').className
      if (this.lnbMinimize) {
        document.querySelector('#app').className = appClassName + ' app_min_1280'
      } else {
        document.querySelector('#app').className = appClassName.replace('app_min_1280', '')
      }
    },
    'model.traffic' (value) {
      if (value > this.availableTraffic) {
        this.model.traffic = this.availableTraffic
      }
    },
    startDateType (value) {
      if (value === 'instantly') {
        this.model.startDate = utcToLocalTimezone(new Date(), this.applyData.timezoneName)
        // this.model.endDate = this.formatDate(addDays(new Date(), END_DATE_DIFF))
      } else {
        if (!this.isEditMode) {
          this.model.startDate = this.formatDate(addDays(utcToLocalTimezone(new Date(), this.applyData.timezoneName), 1))
          const endDate = this.model.endDate.split(' ')[0]
          if (endDate === this.formatDate(utcToLocalTimezone(new Date(), this.applyData.timezoneName))) {
            this.model.endDate = this.formatDate(addDays(utcToLocalTimezone(new Date(), this.applyData.timezoneName), END_DATE_DIFF + 1))
          }
        }
      }
    }
  },
  methods: {
    ...mapActions([
      'storeABList'
    ]),
    getTargetList (abaid) {
      return getABTargetList(abaid).then(list => {
        return list
      }).catch(e => {
        return []
      })
    },
    onTimezoneChange (value) {
      this.model.timezone = value.offset
      this.model.timezoneName = value.timezone
    },
    onSliderInput (value) {
      this.model.traffic = value * 2
    },
    getValicationErrorText (errorMessage) {
      const keyPrefix = 'common.validationErrorText.ab.addPage'
      let messageKey

      switch (errorMessage) {
        case 'NOEXIST':
          messageKey = `${keyPrefix}.notExists`
          break
        case 'INVALID URL':
          messageKey = `${keyPrefix}.invalidUrl`
          break
        case 'FULL TRAFFIC':
          messageKey = `${keyPrefix}.fullTraffic`
          break
        case 'URL ERROR':
          messageKey = `${keyPrefix}.urlError`
          break
        case 'EXIST URL':
          messageKey = `${keyPrefix}.existUrl`
          break
        default :
          messageKey = 'common.validationErrorText.error'
      }
      return this.$t(messageKey)
    },
    formatDate (value) {
      return format(value, 'YYYY-MM-DD')
    },
    UIFormatDate (value) {
      return format(value, DATE_FORAMT)
    },
    toggleDatePicker (type) {
      switch (type) {
        case 'start':
          if (!this.showStartDatePicker) {
            triggerClickEvent()
          }
          this.showStartDatePicker = !this.showStartDatePicker
          break
        case 'end':
          if (!this.showEndDatePicker) {
            triggerClickEvent()
          }
          this.showEndDatePicker = !this.showEndDatePicker
          break
      }
    },
    onStartDateOptionChange (value) {
      this.startDateType = value
    },
    onCalendarDateChange (dateType, value) {
      let dateValue = parse(value)

      if (dateType === 'startDate') {
        dateValue = startOfDay(dateValue)
      } else {
        dateValue = endOfDay(dateValue)
      }
      dateValue = format(dateValue, DATE_FORAMT)
      if (dateType === 'startDate') {
        if (dateValue.split(' ')[0] > this.model['endDate'].split(' ')[0]) {
          this.onCalendarDateChange('endDate', addDays(parse(value), END_DATE_DIFF))
        }
      }
      console.log(dateValue)
      this.model[dateType] = dateValue
    },
    onRegistration () {
      let isTargetError = false
      if (this.targetList.length > 0) {
        this.targetList.forEach((item, idx) => {
          console.log(item)
          if (!item.aid) {
            this.$set(this.targetList, idx, {
              ...this.targetList[idx],
              error: true
            })
            isTargetError = true
          }
        })
      }
      if (isTargetError) {
        return false
      }

      if (!this.model.pageName) {
        this.validation.bPageNameNull = true
        return false
      } else {
        this.validation.bPageNameNull = false
      }

      if (!this.model.url) {
        this.validation.bPageUrlNull = true
        return false
      } else {
        this.validation.bPageUrlNull = false
      }

      if (!this.validationUrl) {
        this.validation.bPageUrlFormatError = true
        return false
      } else {
        this.validation.bPageUrlFormatError = false
      }

      if (!this.validationDomain) {
        this.validation.DomainNotMatch = true
        return false
      } else {
        this.validation.DomainNotMatch = false
      }

      const values = {
        aid: this.applyData._id,
        url: this.model.url,
        pageName: this.model.pageName,
        traffic: this.model.traffic,
        abTestType: this.abTestType,
        timezone: this.model.timezone,
        timezoneName: this.model.timezoneName,
        startDate: this.model.startDate ? format(this.model.startDate, 'YYYY-MM-DD') : null,
        endDate: this.model.endDate ? format(this.model.endDate, 'YYYY-MM-DD') : null
      }

      // todo: i18n
      addPage(values)
        .then(inserted => {
          const targetList = this.targetList.map(item => {
            return {
              aid: item.aid,
              userId: this.userData.userId,
              userNum: this.userData.userNum,
              regDate: item.regDate,
              sid: this.applyData.sid,
              deleted: false
            }
          })
          addABTarget({ abid: inserted._id, data: targetList })
            .then(res => {})
            .catch(console.error)
            .finally(() => {
              this.showTrackingCode = true
            })
        })
        .catch(err => {
          console.log(err)
          const errorMessage = this.getValicationErrorText(err.message)
          openDialog({
            type: 'alert',
            iconClass: 'icon--dialog-warning',
            title: this.$t('dialog.common.title'),
            description: errorMessage
          })
        })
    },
    onModify () {
      let isTargetError = false
      if (this.targetList.length > 0) {
        this.targetList.forEach((item, idx) => {
          if (!item.aid) {
            this.$set(this.targetList, idx, {
              ...this.targetList[idx],
              error: true
            })
            isTargetError = true
          }
        })
      }

      if (isTargetError) {
        return false
      }

      if (!this.model.pageName) {
        this.validation.bPageNameNull = true
        return false
      } else {
        this.validation.bPageNameNull = false
      }
      const values = {
        orgAid: this.aid,
        abAid: this.abAid,
        pageName: this.model.pageName,
        abTestType: this.abTestType,
        timezone: this.model.timezone,
        timezoneName: this.model.timezoneName,
        startDate: this.model.startDate ? format(this.model.startDate, 'YYYY-MM-DD') : null,
        endDate: this.model.endDate ? format(this.model.endDate, 'YYYY-MM-DD') : null,
        traffic: this.model.traffic
      }
      modifyPage(values).then(res => {
        if (this.isEditTarget) {
          modifyABTarget({ abid: this.abAid, data: this.targetList }).then(res => {
            this.onAddorEditDone()
          })
        } else {
          this.onAddorEditDone()
        }
      }).catch(err => {
        const errorMessage = this.getValicationErrorText(err.message)
        openDialog({
          type: 'alert',
          iconClass: 'icon--dialog-warning',
          title: this.$t('dialog.common.title'),
          description: errorMessage
        })
      })
    },
    onAddorEditDone () {
      this.storeABList(this.aid).then(() => {
        this.$router.push({ name: 'ab.list', params: this.$route.params })
      }).catch(console.error)
    },
    onAddOrEditCancel () {
      this.$router.go(-1)
    },
    onTargetUpdate (targetList) {
      console.log('onTargetUpdate : ', targetList)
      this.targetList = targetList
    }
  }
}
</script>

<style lang="scss" scoped>
.range-slider {
  width: 90px;
  &.readonly {
    pointer-events: none;
    opacity: 0.3;
  }

  /deep/ .range-slider-rail {
    width: 90px;
    height: 8px;
    border-radius: 4px;
    background-color: #d7d7d7;
  }
  /deep/ .range-slider-fill {
    height: 8px;
    border-radius: 4px;
  }
  /deep/ .range-slider-knob {
    width: 18px;
    height: 18px;
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.29), 0 2px 3px 0 rgba(0, 0, 0, 0.22);
    background-color: #ffffff;
  }
  &.a-page /deep/ .range-slider-fill {
    background-color: #9fce12;
  }

  &.b-page /deep/ .range-slider-fill{
    background-color: #1199af;
  }
}

.ab__registration {
  box-sizing: border-box;
  padding: 30px;
  overflow-y: auto;

  h2 {
    font-size: 22px;
    font-weight: bold;
    color: #444444;
  }

  .heatmap__control_guide {
    display: flex;
    align-items: center;
    cursor: initial;
    margin-top: 20px;

    > span, a {
      color: #767676;
      font-family: Roboto;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 12px; /* 100% */
    }

    > span {
      margin-right: 8px;
    }

    > a {
      cursor: pointer;
    }

    > .icon.icon--bc-step {
      width: 14px;
      height: 14px;
      padding: 0;
      background-position: 0px center;
    }
  }

  .ab-page__set {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;

    .validation__text {
      @include validErrorText;
      transform: translate(5px, 5px);
      line-height: 1.46;
    }

    .a-page-info--wrap, .b-page-info--wrap {
      position: relative;
      width: calc(50% - 15px);
      border-radius: 14px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #ffffff;
      padding: 25px;
      padding-left: 125px;
      box-sizing: border-box;

      .label {
        position: absolute;
        top: 25px;
        left: 25px;
        width: 75px;
        height: 75px;
        border-radius: 7px;

        i {
          @include positionXYcenter;
          font-size: 30px;
          font-weight: bold;
          color: #ffffff;
        }
      }

      .input--wrap {
        &:nth-last-of-type(1) {
          margin-top: 40px;
        }

        label {
          display: block;
          font-size: 13px;
          font-weight: bold;
          color: #767676;
        }
        input {
          border-radius: 6px;
          width: 100%;
          height: 38px;
          margin-top: 5px;
          box-sizing: border-box;
          font-size: 14px;
          padding-left: 12px;
          padding-right: 38px;

          &.error {
            border-color: $errorColor !important;
          }
        }
      }

      &.a-page-info--wrap {
        .label {
          background-color: #9fce12;
        }
        .input--wrap {
          input {
            border: solid 1px #d7d7d7;
            background-color: #e6e6e6;
            color: #999999;
          }
        }
      }

      &.b-page-info--wrap {
        .label {
          background-color: #1199af;
        }
        .input--wrap {
          position: relative;

          input {
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            color: #2f2f2f;

            &:focus {
              border-color: #289eef;
            }

            &.readonly {
              border: solid 1px #d7d7d7;
              background-color: #e6e6e6;
              color: #999999;
            }
          }

          .icon--close14 {
            position: absolute;
            top: 21px;
            right: 2px;
            padding: 9px 12px;
            cursor: pointer;
            background-position: center;
            background-color: transparent;
          }
        }
      }
    }
  }

  .ab-option__set {
    margin-top: 30px;
    width: 100%;
    border-radius: 14px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #ffffff;
    padding: 25px;
    padding-bottom: 32px;
    box-sizing: border-box;
    position: relative;
    display: inline-block;

   .ab-traffic--wrap {

      .desc {
        font-size: 14px;
        font-weight: bold;
        line-height: 1.5;
        color: #767676;
      }

      .fig--wrap {
        margin-top: 25px;
        display: flex;
        justify-content: center;

        .ab-traffic-area {
          position: relative;
          display: flex;
          align-items: center;

          padding-right: 50px;
          border-right: 1px solid #eaeaea;
          flex-direction: column;
        }

        .option--wrap {
          display: flex;
          align-items: center;
        }

        .label {
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
          color: #888888;
        }

        .icon--link-red-size20 {
          min-width: 20px;
        }

        .input--wrap.type__range {
          margin-left: 30px;

          .range {
            padding-left: 23px;
            position: relative;
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
            color: #444444;

            &:nth-last-of-type(1) {
              margin-top: 15px;
            }

            label {
              @include positionYcenter;
              left: 0;
            }
          }
        }

        .input--wrap.type__number {
          margin-left: 15px;
          font-size: 14px;
          color: #2f2f2f;
          min-width: 128px;

          input {
            display: inline-block;
            width: 62px !important;
            height: 38px;
            border-radius: 6px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            box-sizing: border-box;
            padding-left: 15px;

            &.readonly {
              pointer-events: none;
              border: solid 1px #d7d7d7;
              background-color: #e6e6e6;
              color: #999;
            }
          }

          span {
            display: inline-block;
            margin-left: 8px;
            color: #767676;
          }
        }

        .fig-traffic-graph--wrap {
          position: relative;
          margin-left: 32px;

          /deep/ .pie-chart {
            pointer-events: none;
          }
        }

        .legend--wrap {
          white-space: nowrap;

          li {
            margin-top: 18px;
            margin-left: 26px;
            font-size: 13px;
            color: #444;

            &:nth-of-type(1) {
              position: relative;
              margin-top: 0;

              .tooltip {
                position: absolute;
                top: -2px;
                left: 100%;
                margin-left: 4px;
                height: 17px;
              }
            }

            .circle {
              display: inline-block;
              width: 11px;
              height: 11px;
              position: relative;
              top:1px;
              background-color: #555555;
              border-radius: 50%;
              margin-right: 9px;

              &.color-used {
                background-color: #555;
              }

              &.color-a-page {
                background-color: #9fce12;
              }

              &.color-b-page {
                background-color: #1199af;
              }
            }

            .value {
              display: inline-block;
              margin-right: 11px;
            }
          }
        }
      }

      .emphysize {
        margin-top: 25px;
        text-align: center;
        font-size: 13px;
        line-height: 1.54;
        height: 40px;
        color: $brandColor;
      }
    }

    .ab-period--wrap {
      float: left;
      min-width: 400px;

      p.title {
        padding-left: 25px;
        font-size: 14px;
        font-weight: 500;
        color: #888888;
        display: inline-block;
        line-height: 21px;
        position: relative;

        .tooltip {
          margin: 1px 0 -1px 3px;
          vertical-align: middle;
        }
      }

      p.description {
        margin: 5px 0 57px;
        padding-left: 25px;
        font-size: 14px;
        font-weight: bold;
        line-height: 1.5;
        color: #767676;
      }

      ul {
        margin-top: 0;
        padding-left: 25px;
        font-size: 13px;

        li {
          margin: 53px 0 30px;

          &:last-child {
            margin-top: 30px;
          }

          &.disable {
            margin-top: 45px;
          }

          span.start_select {
            vertical-align: top;
            margin-top: 13px;
          }

          .input__label {
            display: inline-block;
            width: 90px;
            vertical-align: middle;
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
            color: #888888;

          }

          .start__wrap {
            display: inline-block;

            label {
              height: 38px;

              span {
                margin-top: 13px;
              }
            }
          }

          label {
            position: relative;
            display: inline-block;
            white-space: nowrap;
            width: 100%;

            &:nth-of-type(1) {
              margin-right: 40px;
              width: 150px;
            }

            & > .icon:not(.icon--calendar-input){
              position: absolute;
              top: 50%;
              left: 0;
              transform: translateY(-50%);
            }

            & > span {
              display: inline-block;
              margin-left: 26px;
            }

            &.end-date-item {
              &.disabled {
                opacity: 0.5;
                pointer-events: none;
              }
            }
          }

          &.timezone--wrap {
            margin-top: 30px;
            margin-bottom: -15px;
            .timezone-select {
              vertical-align: middle;
            }
          }

          .datepicker--wrap {
            @include positionYcenter;
            display: inline-block;

            &.data__start-date {
              margin-left: 15px;

              .date_picker {
                top: -293px;
                left: 50%;
              }
            }

            & > .icon {
              position: absolute;
              top: 50%;
              left: 9px;
              transform: translateY(-50%);
            }

            & > input[type=text] {
              width: 129px;
              height: 38px;
              border-radius: 6px;
              background-color: #ffffff;
              border: 1px solid #bbbbbb;
              box-sizing: border-box;
              text-align: right;
              font-size: 14px;
              color: #2f2f2f;
              padding-right: 12px;

              &:disabled {
                background-color: #e6e6e6;
                border: 1px solid #d7d7d7;
                color: #999999;
              }

              &:focus {
                border-color: #289eef;
              }
            }

            .date_picker {
              top: -269px;
              left: 50%;
            }
          }
        }
      }
    }
  }

  .buttons--wrap {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-top: 30px;
    text-align: center;

    .button--wrap {
      &.end {
        text-align: end;
      }
    }

  }

  .btn__confirm {
    background-color: $brandColor;
    border: 0 !important;
    margin-left: 5px;

    &:hover {
      background-color: $brandColor !important;
    }

    .btn-text {
      color: #fff;
    }
  }
}

/deep/ .ui_select {
  cursor: pointer;
  text-align: left;
  display: inline-block;

  &.disabled {
    pointer-events: none;

    .item--wrap {
      background-color: #e6e6e6;
      opacity: 1;
      border: 1px solid #d7d7d7;

      .selected-item {
        color: #999999;
      }
    }
  }
}

@media (max-width: 1280px)  {
  .fig--wrap {
    margin-top: 46px !important;
  }
  .ab-traffic-area {
    margin-left: 0px !important;
  }
  .option--wrap {
    display: inline-block !important;
    width: 450px;

    span.label {
      display: inline-block;
      position: relative;
      top: -23px;
      margin-left: 0px !important;
    }

    div.type__range {
      display: inline-block;

      & .range {
        padding-left: 13px !important;
      }
      & .range-slider {
        width: 90px;
      }
    }

    i.icon {
      display: inline-block;
      position: relative;
      top: -19px;
      margin: 0 10px 0 0px;
    }

    div.type__number {
      display: inline-block;
      position: relative;
      top: -24px;
      margin-left: 0px !important;

      input {
        width: 49px !important;
      }
    }

    figure {
      display: inline-block;
      margin-top: 50px;
      margin-left: 0px !important;
    }

    ul.legend--wrap {
      display: inline-block;
      position: relative;
      top: -71px;
      left: 26px;
    }
  }

  p.emphysize {
    width: 400px;
  }

  .ab-period--wrap {
    margin-top: 20px;
    margin-left: 20px;

    span.input__label {
      width: 80px !important;
    }
  }
}

@media (min-width: 1280px) and (max-width: 1440px) {
  .fig--wrap {
    margin-top: 46px !important;
  }
  .ab-traffic-area {
    margin-left: 0px !important;
  }
  .option--wrap {
    display: inline-block !important;
    width: 450px;

    span.label {
      display: inline-block;
      position: relative;
      top: -23px;
      margin-left: 0px !important;
    }

    div.type__range {
      display: inline-block;

      & .range {
        padding-left: 13px !important;
      }
      & .range-slider {
        width: 90px;
      }
    }

    i.icon {
      display: inline-block;
      position: relative;
      top: -19px;
      margin: 0 10px 0 0px;
    }

    div.type__number {
      display: inline-block;
      position: relative;
      top: -24px;
      margin-left: 0px !important;

      input {
        width: 49px !important;
      }
    }

    figure {
      display: inline-block;
      margin-top: 50px;
      margin-left: 0px !important;
    }

    ul.legend--wrap {
      display: inline-block;
      position: relative;
      top: -71px;
      left: 26px;
    }
  }

  p.emphysize {
    width: 400px;
  }

  .ab-period--wrap {
    margin-top: 20px;
    margin-left: 20px;

    span.input__label {
      width: 80px !important;
    }
  }
}

@media (min-width: 1440px) and (max-width: 1599px) {
  .fig--wrap {
    margin-top: 46px !important;
  }
  .ab-traffic-area {
    margin-left: 0px !important;
  }
  .option--wrap {
    display: inline-block !important;
    width: 450px;
    //margin-left: 142px;

    span.label {
      display: inline-block;
      position: relative;
      top: -23px;
      margin-left: 0px !important;
    }

    div.type__range {
      display: inline-block;

      & .range {
        padding-left: 13px !important;
      }
      & .range-slider {
        width: 90px;
      }
    }

    i.icon {
      display: inline-block;
      position: relative;
      top: -19px;
      margin: 0 11px 0 0px;
    }

    div.type__number {
      display: inline-block;
      position: relative;
      top: -24px;
      margin-left: 0px !important;

      input {
        width: 49px !important;
      }
    }

    figure {
      display: inline-block;
      margin-top: 50px;
      margin-left: 0px !important;
    }

    ul.legend--wrap {
      display: inline-block;
      position: relative;
      top: -71px;
      left: 26px;
    }
  }

  p.emphysize {
    width: 400px;
  }

  .ab-period--wrap {
    margin-top: 20px;
    margin-left: 20px;

    span.input__label {
      width: 80px !important;
    }
  }
}

html[lang='ja'] {
  .ab__registration {
    .ab-option__set {
      .ab-traffic--wrap {
        .option--wrap{
          .label {
            font-size: 12px;
            min-width: 108px;
          }
        }
        .fig--wrap {
          .input--wrap {
            &.type__range {
              margin-left: 24px;
            }
          }
        }
      }
    }
    .ab-period--wrap {
      ul {
        li {
          .input__label {
            min-width: 85px;
          }
        }
      }
    }
  }
}

html[lang='en'] {
  .ab__registration {
    .ab-option__set {
      .ab-traffic--wrap {
        .fig--wrap {
          .label {
            min-width: 75px;
          }
        }
      }
    }
  }
}

</style>
